// src/redux/slices/admin/exerciseSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../api';

// Async thunk to fetch exercises
export const fetchExercises = createAsyncThunk(
  'exercises/fetchExercises',
  async ({page = 1, searchTerm=''}, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/exercises-list?page=${page}`, {searchTerm});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch exercises');
    }
  }
);

// Async thunk to delete an exercise
export const deleteExercise = createAsyncThunk(
  'exercises/deleteExercise',
  async (exerciseId, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`${process.env.REACT_APP_API_BASE_URL}/admin/exercise/`+exerciseId);
      dispatch(fetchExercises()); // Fetch updated exercise list after deletion
      return exerciseId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete exercise');
    }
  }
);

// Async thunk to create a new exercise
export const createExercise = createAsyncThunk(
  'exercises/createExercise',
  async (exerciseData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/exercise`, exerciseData);
      if (response.data.success) {
      	var p = {"page":1, "searchTerm":""};
        dispatch(fetchExercises(p)); // Fetch updated exercise list after creation
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create exercise');
    }
  }
);

// Async thunk to update an existing exercise
export const updateExercise = createAsyncThunk(
  'exercises/updateExercise',
  async (exerciseData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/update-exercise`, exerciseData);
      if (response.data.success) {
        dispatch(fetchExercises()); // Fetch updated exercise list after update
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update exercise');
    }
  }
);

const exerciseSlice = createSlice({
  name: 'exercises',
  initialState: {
    exercises: [],
    currentExercisePage: 1,
    totalExercisePages: 0,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExercises.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExercises.fulfilled, (state, action) => {
        state.loading = false;
        state.exercises = action.payload.data.data || [];
        state.currentExercisePage = action.payload.data.current_page;
        state.totalExercisePages = action.payload.data.last_page;
      })
      .addCase(fetchExercises.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteExercise.fulfilled, (state, action) => {
        state.exercises = state.exercises.filter(exercise => exercise.id !== action.payload);
      })
      .addCase(deleteExercise.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createExercise.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.successMessage = 'Exercise created successfully';
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(createExercise.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateExercise.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.exercises.findIndex(exercise => exercise.id === action.payload.data.id);
          if (index !== -1) {
            state.exercises[index] = action.payload.data; // Update with new data
          }
          state.successMessage = 'Exercise updated successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(updateExercise.rejected, (state, action) => {
        state.error = action.payload; // Handle error on rejection
      });
  },
});

// Export actions and reducer
export const { clearMessages } = exerciseSlice.actions;

export default exerciseSlice.reducer;